/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Table, InputGroup, FormControl, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import * as reportParams from '../../api/ReportParameters';
import * as reports from '../../api/ReceptionReport';
import DateSelector from '../../components/DateSelector';
import Modal from '../../components/Modal';
import DropdownMultiselect from "react-multiselect-dropdown-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GENERATETYPES, REPORTSTATUS } from '../../util/DataModelInterface';
import { getPreviousMonth, localHour, utcHour, formatPeriod, getReportTypeString, IMonthValue } from '../../util/DateUtils';

interface IManageReceptionReportProps {
  intl: any;
}

const ManageReceptionReport = (props: IManageReceptionReportProps) => {
  const [,setState] = useState({}); // just to force re-render when upload status changes
  const [startDate, setStartDate] = useState(getPreviousMonth());
  const [endDate, setEndDate] = useState(getPreviousMonth());
  const [modalOpen, setModalOpen] = useState(false);
  const [reportAutoData, setReportAutoData] = useState([] as reportParams.IListReportParameters[]);
  const [reportManualData, setReportManualData] = useState([] as reportParams.IListReportParameters[]);
  const [scheduleData, setScheduleData] = useState({} as reportParams.ReportSchedules);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [types, setTypes] = useState([] as string[]);
  const [tab, setTab] = useState("manual");
  const [statuses, setStatuses] = useState([] as string[]);
  const [name, setName] = useState('');
  const [orderColumn, setOrderColumn] = useState('operatorName');
  const [orderDirection, setOrderDirection] = useState(1); // 1 | -1
  const [modalText, setModalText] = useState('');

  const intl = useIntl();

  useEffect(() => {
    reportParams.getReportParameters().then((data: any) => {
      if (data) {
        setReportAutoData(data as reportParams.IListReportParameters[]);
      }
    });
    reportParams.getReportParameters().then((data: any) => {
      if (data) {
        data.forEach((row : reportParams.IListReportParameters) => {
          row.autogenReport = false;
          row.autogenEmail = false;
        });
        setReportManualData(data as reportParams.IListReportParameters[]);
      }
    });
    reportParams.getReportSchedules().then((data: any) => {
      if (data) {
        setScheduleData(data as reportParams.ReportSchedules);
      }
    });
  }, []);

  const getDayList = (type : string) => {
    const dayList: JSX.Element[] = [];
    dayList.push(<option value="" key="day-0">{props.intl.formatMessage({id:'day-list.choose'})}</option>);
    for(let i = 0; i<31; i++) {
      // eslint-disable-next-line eqeqeq
      dayList.push(<option key={"day-"+i+1} value={i+1}>{i+1}{props.intl.formatMessage({id:'day-list.'+ type})}</option>);
    }
    return dayList;
  }

  const getTimeList = () => {
    const timeList: JSX.Element[] = [];
    timeList.push(<option value="" key="time-0">{props.intl.formatMessage({id:'time-list.choose'})}</option>);
    for(let i = 0; i<24; i++) {
      // eslint-disable-next-line eqeqeq
      timeList.push(<option key={"time-"+i+1} value={i}>{ (i).toLocaleString('fi-FI', {minimumIntegerDigits: 2, useGrouping:false}) }:00</option>);
    }
    return timeList;
  }

  const formatDateRange = () => { //"2020-01+2020-02"
    let month = startDate.month + 1;
    const monthStr1 = month < 10 ? '0' + month : month;
    month = endDate.month + 1;
    const monthStr2 = month < 10 ? '0' + month : month;
    const value = `${startDate.year}-${monthStr1}+${endDate.year}-${monthStr2}`;
    return value;
  }

  const setGenReport = (operatorId : string, value : boolean) => {
    if(tab === "auto") {
      let data = reportAutoData as reportParams.IListReportParameters[];
      const index = data.findIndex((r) => r.operatorId === operatorId);
      data[index].autogenReport = value;
      setReportAutoData(data);
      reportParams.saveReportParameters((reportAutoData as reportParams.IReportParameters[])[index]).then(() => {
      }).finally(() => {
        setState({});
      });
    } else {
      let data = reportManualData as reportParams.IListReportParameters[];
      const index = data.findIndex((r) => r.operatorId === operatorId);
      data[index].autogenReport = value;
      if (!value) {
        data[index].autogenEmail = value;
      }
      setReportManualData(data);
    }
    setState({});
  }

  const setGenEmail = (operatorId : string, value : boolean) => {
    if(tab === "auto") {
      let data = reportAutoData as reportParams.IListReportParameters[];
      const index = data.findIndex((r) => r.operatorId === operatorId);
      data[index].autogenEmail = value;
      setReportAutoData(data);
      reportParams.saveReportParameters((reportAutoData as reportParams.IReportParameters[])[index]).then(() => {
      }).finally(() => {
        setState({});
      });
    } else {
      let data = reportManualData as reportParams.IListReportParameters[];
      const index = data.findIndex((r) => r.operatorId === operatorId);
      data[index].autogenEmail = value;
      setReportManualData(data);
    }
    setState({});
  }

  const getModal = () =>
    <Modal
      onClose={() => setModalOpen(false)}
      closeOnClickOutside={false}
      width={400}
      height={250}
    >
      <div className="ModalView">
        <div className="spinner"/>
        <div className="message"><FormattedMessage id={modalText}/></div>
      </div>
    </Modal>

  const getDataTable = () => {
    const options: { }[] = [];
    const data = filterRows();
    if (data.length > 0) {
      data.forEach((row : reportParams.IListReportParameters, index) => {
        let reportSuccess = row.lastReport != null && row.lastReport.success;
        let infoText = props.intl.formatMessage({id: 'report-status.REPORTSTATUS'}) + ': ';
        infoText +=  row.lastReport != null ?
                          ( row.lastReport.success ?
                            ( row.lastReport.empty ? props.intl.formatMessage({id: 'report-status.EMPTY'}) :
                              props.intl.formatMessage({id: 'report-status.OK'})) :
                              props.intl.formatMessage({id: 'report-status.' + row.lastReport.info}) ) :
                          props.intl.formatMessage({id:'receptionreports.manage.datatable.noreport'});
        if (row.lastReport && row.lastReport.mailStatus)
        {
          const mailInfo = row.lastReport.mailStatus.info ? '\n[' + row.lastReport.mailStatus.info + ']' : '';
          const mailTime = row.lastReport.mailStatus.time ? `\n${intl.formatDate(row.lastReport.mailStatus.time)} ${intl.formatTime(row.lastReport.mailStatus.time)}` : '';
          infoText += '\n' +
            props.intl.formatMessage({id: 'report-status.MAILSTATUS'}) + ': ' +
            props.intl.formatMessage({id: 'report-status.' + row.lastReport.mailStatus.status}) +
            mailTime +
            mailInfo;
        }
        options.push(
          <tr key={index}>
            <td><InputGroup.Checkbox className="form-check-input" checked={row.autogenReport} onChange={(e: any) => { setGenReport(row.operatorId, e.target.checked) }}/></td>
            <td><InputGroup.Checkbox className="form-check-input"  checked={row.autogenEmail} disabled={!row.autogenReport} onChange={(e: any) => { setGenEmail(row.operatorId, e.target.checked) }}/></td>
            <td>{row.operatorName}</td>
            <td className="listCell">
            <div className="d-flex flex-row align-items-center">
              <span  className= { row.lastReport != null ? (row.lastReport.autoGenerated ? "type" : "type") : "hidden"}>
                <FontAwesomeIcon className="reportIcon"
                                  icon={ row.lastReport != null ? (row.lastReport.autoGenerated ? "print" : "pencil-ruler") : "minus-circle" }
                                  color={ row.lastReport != null ? (row.lastReport.success ? 'var(--serty-color-ok)' : 'var(--serty-color-warning)') : 'var(--serty-grey)' } />

                { row.lastReport != null ?
                  <span>
                    {props.intl.formatMessage({id: 'report-type.' + getReportTypeString(row.lastReport.periodStart, row.lastReport.periodEnd, row.lastReport.autoGenerated)})}
                  </span> :
                  <span className="hidden"></span> }
              </span>
              {( row.lastReport!=null ? (row.lastReport.success ?
                <div className="d-flex flex-row align-items-center">
                  <a href="#" onClick={() => { if(row.lastReport!=null) reports.getReceptionReportExcel(row.operatorId, row.lastReport.reportId)} } >
                    { new Date( row.lastReport.generatedDate ).toLocaleString('fi-FI') + " - " +
                    formatPeriod(row.lastReport.periodStart, row.lastReport.periodEnd, props.intl)}
                  </a>
                </div>:
                new Date( row.lastReport.generatedDate ).toLocaleString('fi-FI')) :
                props.intl.formatMessage({id:'receptionreports.manage.datatable.noreport'}))}
              <div className="reportIcon">
                <button className="btn btn-primary tiny-send-button" disabled={!reportSuccess}
                        onClick={() => {if(row.lastReport!=null) sendReportEmail(row.operatorId, row.lastReport.reportId);}}>
                    <FontAwesomeIcon className="reportIcon"
                    icon="envelope"
                    color="white" />
                  <FormattedMessage id="receptionreports.manage.sendreport"/>
                </button>
              </div>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip className="info-tooltip" id="tooltip-overlay">
                    <div className="info-tooltip-header">{ infoText }</div>
                  </Tooltip>
                }
              >
              <FontAwesomeIcon className="reportStatusIcon"
                                icon={ row.lastReport != null ? (row.lastReport.success ? (row.lastReport.empty ? "dot-circle" : "check-circle") : "exclamation-circle") : "exclamation-circle" }
                                color={ row.lastReport != null ? (row.lastReport.success ? (row.lastReport.empty ? 'var(--serty-color-warning)' : 'var(--serty-color-ok)') : 'var(--serty-color-error)') : 'var(--serty-color-error)' } />
              </OverlayTrigger>
              </div>
            </td>
          </tr>
        );
      });
      return options;
    } else if (reportManualData.length === 0){
      return (<tr className="waitSpinner"><td colSpan={4}><div className="spinner"/></td></tr>);
    }
    return (<tr className="waitSpinner"><td colSpan={4}><FormattedMessage id="receptionreports.manage.nodata"/></td></tr>);
  }

  const sendReportEmail = (operatorId: string, reportId: string) => {
    setModalText("receptionreports.manage.sending");
    setModalOpen(true);

    const lang = props.intl.locale; // This is not used yet in elsewhere, should it be?
    reports.sendReceptionReportEmail(operatorId, reportId, lang).then(() => {
      reportParams.getReportParameters().then((data: any) => {
        if (data) {
          let newdata = reportManualData;
          newdata.forEach(function (row, i) {
            row.lastReport = data[i].lastReport;
          });
          setReportManualData(newdata);
          setReportAutoData(data);
        }
      }).finally(() => {
        setModalOpen(false);
      });
    });
  }

  const comparer = (a: reportParams.IListReportParameters, b: reportParams.IListReportParameters) => {
    let value = 0;
    if (orderColumn !== '') {
      switch(orderColumn) {
        case 'autogenReport':
          const reportA = a.autogenReport ? 1 : 0;
          const reportB = b.autogenReport ? 1 : 0;
          value = reportA - reportB;
          break;
        case 'autogenEmail':
          const emailA = a.autogenEmail ? 1 : 0;
          const emailB = b.autogenEmail ? 1 : 0;
          value = emailA - emailB;
          break;
        case 'operatorName':
          value = a.operatorName.localeCompare(b.operatorName);
          break;
        case 'lastReport':
          const dateA = a.lastReport ? new Date(a.lastReport.generatedDate) : new Date('1900-01-01');
          const dateB = b.lastReport ? new Date(b.lastReport.generatedDate) : new Date('2099-12-31');
          value = dateA.getTime() - dateB.getTime();
      }
    }
    return orderDirection * value;
  }

  const getTypeSelectList = () => {

    const options: { key: string; label: any; }[] = [];

    GENERATETYPES.forEach((type) =>
      options.push( { key: `${type}`, label: props.intl.formatMessage({id:`report-type.${type}`}) }
    ));

    return(
      <div className="dropdown-div">
        <DropdownMultiselect
        options={options}
        name="reporttypes"
        placeholder= {props.intl.formatMessage({id:'report-createtype.choose'})}
        showSelectToggle="false"
        handleOnChange={(selected: any) => setTypes(selected)}
        />
      </div>);
  }

  const getStatusSelectList = () => {

    const options: { key: string; label: any; }[] = [];

    REPORTSTATUS.forEach((status) =>
      options.push( { key: `${status}`, label: props.intl.formatMessage({id:`report-status.${status}`}) }
    ));

    return(
      <div className="dropdown-div">
        <DropdownMultiselect
        options={options}
        name="reportstatuses"
        placeholder= {props.intl.formatMessage({id:'report-status.choose'})}
        showSelectToggle="false"
        handleOnChange={(selected: any) => setStatuses(selected)}
        />
      </div>);
  }

  const typeFilter = (row: reportParams.IListReportParameters) => {
    if (types.length === 0 || 
       (row.lastReport &&
       (types.includes(getReportTypeString(row.lastReport?.periodStart, row.lastReport?.periodEnd, row.lastReport?.autoGenerated))))) {
      return true;
    }
    return false;
  }

  const statusFilter = (row: reportParams.IListReportParameters) => {
    if (statuses.length === 0 
      || ((row.lastReport && row.lastReport.success && !row.lastReport.empty && statuses.includes('OK'))
      || (row.lastReport && !row.lastReport.success && statuses.includes('FAIL'))
      || (!row.lastReport && statuses.includes('FAIL'))
      || (row.lastReport && row.lastReport.empty && statuses.includes('EMPTY')))) {
      return true;
    }
    return false;
  }

  const nameFilter = (row: reportParams.IListReportParameters) => {
    if (name === '' || row.operatorName.toLowerCase().includes(name.toLowerCase())) {
      return true;
    }
    return false;
  }

  const filterRows = () => {
    let data: reportParams.IListReportParameters[] = [];
    const reportData = (tab === "auto") ? reportAutoData : reportManualData;
    if (reportData && Array.isArray(reportData)) {
      data = reportData.filter(nameFilter).filter(typeFilter).filter(statusFilter).sort(comparer);
    }
    return data;
  }

  const isReportCheckboxAllSelected = () => {
    const data = filterRows();
    let allSelected = data.length > 0;
    data.forEach((row) => {
      if (!row.autogenReport) {
        allSelected = false;
      }
    });
    return allSelected;
  }

  const isEmailCheckboxAllSelected = () => {
    const data = filterRows();
    let allSelected = data.length > 0;
    data.forEach((row) => {
      if (!row.autogenEmail) {
        allSelected = false;
      }
    });
    return allSelected;
  }

  const handleReportCheckboxClick = (event: any) => {
    const data = filterRows();
    if (isReportCheckboxAllSelected()) {
      data.forEach((row) => row.autogenReport = false);
      data.forEach((row) => row.autogenEmail = false);
    } else {
      data.forEach((row) => row.autogenReport = true);
    }
    event.target.checked = isReportCheckboxAllSelected();
    setState({});
  }

  const handleEmailCheckboxClick = (event: any) => {
    const data = filterRows();
    if (isEmailCheckboxAllSelected()) {
      data.forEach((row) => row.autogenEmail = false);
    } else {
      data.forEach((row) => row.autogenEmail = row.autogenReport);
    }
    event.target.checked = isEmailCheckboxAllSelected();
    setState({});
  }

  const filterByName = (value: string) => {
    setName(value);
    setState({});
  }

  const manualReports = () => {
    setModalText("receptionreports.manage.reporting");
    setModalOpen(true);
    let reports: reportParams.IManualReportParameters = { reportPeriod: formatDateRange(), reportLanguage: "fi-FI", reports: []};
    (reportManualData as reportParams.IListReportParameters[]).forEach(async (row : reportParams.IListReportParameters, index: number) => {
      if(row.autogenReport) {
        const report : reportParams.IReportParameters = {operatorId: "", autogenEmail: false, autogenReport: true };
        report.autogenEmail = row.autogenEmail;
        report.operatorId = row.operatorId;
        reports.reports.push( report );
      } // if
    }) // forEach
    reportParams.makeManualReports(reports).then(() => {
      reportParams.getReportParameters().then((data: any) => {
        if (data) {
          let newdata = reportManualData;
          newdata.forEach(function (row, i) {
            row.lastReport = data[i].lastReport;
          });
          setReportManualData(newdata);
          setReportAutoData(data);
        }
      }).finally(() => {
        setModalOpen(false);
      });
    });
  }

  const getOrderIcon = (column: string) => {
    let icon;
    if (column === orderColumn) {
      icon = orderDirection === 1 ?
        <FontAwesomeIcon className="icon" icon="sort-up" color="white" /> : <FontAwesomeIcon className="icon" icon="sort-down" color="white" />
    } else {
      icon = <FontAwesomeIcon className="icon" icon="sort" color="lightgray" />
    }
    return icon;
  }

  const setOrdering = (column: string) => {
    if (orderColumn !== column) {
      setOrderColumn(column);
      setOrderDirection(1);
    } else {
      setOrderDirection(-orderDirection);
    }
  }

  return (
    <div>
      { modalOpen && getModal() }
      <h1>
        <FormattedMessage id="receptionreports.header"/> -
        <FormattedMessage id="receptionreports.manage.header"/>
      </h1>

      <div className="settings">
        <Tabs defaultActiveKey="manual" id="receptionreport-manage-tabs" onSelect={(eventKey: any) => setTab(eventKey ? eventKey : "manual")}>
          <Tab eventKey="manual" title={props.intl.formatMessage({id:'receptionreports.manage.manual'})}>
            <p className="tip">
              <FontAwesomeIcon className="tipIcon" icon={"info-circle" }/>
              <FormattedMessage id="receptionreports.manage.manual.tip"/>
            </p>
            <div>
              <label><FormattedMessage id="receptionreports.manage.monthrange"/></label>
              <div className="d-flex flex-row row">
                <div className="col-md-6 datepicker-input">
                  <DateSelector startPeriod={startDate} endPeriod={endDate}
                                onChange={(start: IMonthValue, end: IMonthValue) => { setStartDate(start); setEndDate(end); } } 
                                dateFormat={'yyyy-MM'} isStartAndEnd={true}/>
                </div>
              </div>
            </div>
            <div className="form-buttons d-flex flex-row">
              <button className="btn btn-primary ml-auto"
                      onClick={() => {
                      manualReports();}}>
                <FormattedMessage id="receptionreports.manage.makereports"/>
              </button>
            </div>
          </Tab>
          <Tab eventKey="auto" title={props.intl.formatMessage({id:'receptionreports.manage.auto'})}>
            <p className="tip">
              <FontAwesomeIcon
                  className="tipIcon"
                  icon={"info-circle" }/>
              <FormattedMessage id="receptionreports.manage.auto.tip"/>
            </p>
            <div className="d-flex flex-row row">
              <div className="col-md-2 row-label"><label></label></div>
              <div className="col-md-6">
                  <label htmlFor="day-select"><FormattedMessage id="receptionreports.manage.sendingday"/></label>
              </div>
              <div className="col-md-4">
                <label htmlFor="time-select"><FormattedMessage id="receptionreports.manage.sendingtime"/></label>
              </div>
            </div>
            <div className="d-flex flex-row row">
              <div className="col-md-2 row-label">
                <label><FormattedMessage id="receptionreports.manage.monthly"/></label>
              </div>
              <div className="col-md-6">
                <select className="day-select form-control" id="day-select" value={scheduleData.monthly?.dayOfMonth}
                  onChange={(e) => {
                    scheduleData.monthly.dayOfMonth = +e.target.value;
                    setScheduleData(scheduleData);
                    reportParams.saveReportSchedule(scheduleData.monthly).then(() => {
                    }).finally(() => {
                      setState({});
                    });
                  }}>
                  { getDayList('day') }
                </select>
              </div>
              <div className="col-md-4">
                <select className="time-select form-control" id="time-select" value={localHour(scheduleData.monthly?.hour)}
                  onChange={(e) => {
                    scheduleData.monthly.hour = utcHour(e.target.value);
                    setScheduleData(scheduleData);
                    reportParams.saveReportSchedule(scheduleData.monthly).then(() => {
                    }).finally(() => {
                      setState({});
                    });
                  }}>
                  { getTimeList() }
                </select>
              </div>
            </div>
            <div className="d-flex flex-row row">
              <div className="col-md-2 row-label">
                <label><FormattedMessage id="receptionreports.manage.quarterly"/></label>
              </div>
              <div className="col-md-6">
                <select className="day-select form-control" id="day-select" value={scheduleData.quarterly?.dayOfMonth}
                  onChange={(e) => {
                    scheduleData.quarterly.dayOfMonth = +e.target.value;
                    setScheduleData(scheduleData);
                    reportParams.saveReportSchedule(scheduleData.quarterly).then(() => {
                    }).finally(() => {
                      setState({});
                    });
                  }}>
                  { getDayList('quarter') }
                </select>
              </div>
              <div className="col-md-4">
                <select className="time-select form-control" id="time-select" value={localHour(scheduleData.quarterly?.hour)}
                  onChange={(e) => {
                    scheduleData.quarterly.hour = utcHour(e.target.value);
                    setScheduleData(scheduleData);
                    reportParams.saveReportSchedule(scheduleData.quarterly).then(() => {
                    }).finally(() => {
                      setState({});
                    });
                  }}>
                  { getTimeList() }
                </select>
              </div>
            </div>
            <div className="d-flex flex-row row">
              <div className="col-md-2 row-label">
                <label><FormattedMessage id="receptionreports.manage.yearly"/></label>
              </div>
              <div className="col-md-6">
                <select className="day-select form-control" id="day-select" value={scheduleData.yearly?.dayOfMonth}
                  onChange={(e) => {
                    scheduleData.yearly.dayOfMonth = +e.target.value;
                    setScheduleData(scheduleData);
                    reportParams.saveReportSchedule(scheduleData.yearly).then(() => {
                    }).finally(() => {
                      setState({});
                    });
                  }}>
                  { getDayList('year') }
                </select>
              </div>
              <div className="col-md-4">
                <select className="time-select form-control" id="time-select" value={localHour(scheduleData.yearly?.hour)}
                  onChange={(e) => {
                    scheduleData.yearly.hour = utcHour(e.target.value);
                    setScheduleData(scheduleData);
                    reportParams.saveReportSchedule(scheduleData.yearly).then(() => {
                    }).finally(() => {
                      setState({});
                    });
                  }}>
                  { getTimeList() }
                </select>
              </div>
            </div>
          </Tab>
        </Tabs>
      </div>
      <h3><FormattedMessage id="receptionreports.manage.receptionreports"/></h3>
      <div className="table-responsive table-min-height">
        <Table size="sm" className="table table-striped table-bordered">
          <thead className="reportDataHeader">
            <tr>
              <th>
                <span className="sortable" onClick={() => setOrdering('autogenReport')}>
                  <FontAwesomeIcon icon="file-alt"/>{getOrderIcon('autogenReport')}
                </span>
              </th>
              <th>
                <span className="sortable" onClick={() => setOrdering('autogenEmail')}>
                  <FontAwesomeIcon icon="at"/>{getOrderIcon('autogenEmail')}
                </span>
              </th>
              <th>
                <span className="sortable" onClick={() => setOrdering('operatorName')}>
                  <FormattedMessage id="receptionreports.manage.datatable.collectionpointoperator"/>{getOrderIcon('operatorName')}
                </span>
              </th>
              <th>
                <span className="sortable" onClick={() => setOrdering('lastReport')}>
                  <FormattedMessage id="receptionreports.manage.datatable.latestreport"/>{getOrderIcon('lastReport')}
                </span>
              </th>
            </tr>
            <tr>
              <th>
                <InputGroup.Checkbox className="form-check-input" disabled={tab === 'auto'} onClick={(event: any) => handleReportCheckboxClick(event)} />
              </th>
              <th>
                <InputGroup.Checkbox className="form-check-input" disabled={tab === 'auto'} onClick={(event: any) => handleEmailCheckboxClick(event)} />
              </th>
              <th>
                <FormControl
                  placeholder={props.intl.formatMessage({id: 'receptionreports.manage.datatable.findbyname'})}
                  value={name}
                  onChange={(event) => filterByName(event.target.value)}/>
              </th>
              <th className="listCell"><div className="d-flex flex-row"> { getTypeSelectList() } { getStatusSelectList() }</div></th>
            </tr>
          </thead>
          <tbody className="reportData">
              { getDataTable() }
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export default injectIntl(ManageReceptionReport);
